import {
  submitEmailBackInStockSubscription,
  submitEmailMarketingConsent,
  submitSmsBackInStockSubscription,
  submitSmsMarketingConsent,
} from './boh-back-in-stock-klaviyo.js';
import { createBackInStockTranslator } from './boh-back-in-stock-messages.js';
import { normalisePhoneNumber } from './boh-back-in-stock-phone.js';

class BackInStock extends HTMLElement {
  constructor() {
    super();

    this.currentVariantId = null;
    this.productForm = null;
    this.form = null;
    this.emailInput = null;
    this.phoneInput = null;
    this.submitButton = null;
    this.triggerButton = null;
    this.addToCartButton = null;
    this.closeButton = null;
    this.acceptsMarketingInput = null;
    this.phoneErrorElement = null;
    this.statusElement = null;
    this.lastTriggerButton = null;
    this.variantEventSource = null;
    this.t = createBackInStockTranslator(window.Bohomoon?.backInStockMessages);

    this.onSubmit = this.onSubmit.bind(this);
    this.onVariantChanged = this.onVariantChanged.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onPhoneInput = this.onPhoneInput.bind(this);
    this.onPopoverToggle = this.onPopoverToggle.bind(this);
    this.onDocumentVariantChanged = this.onDocumentVariantChanged.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.emailInput = this.querySelector('input[type="email"]');
    this.phoneInput = this.querySelector('input[type="tel"]');
    this.submitButton = this.querySelector('[type="submit"]');
    this.closeButton = this.querySelector('[data-bis-close]');
    this.acceptsMarketingInput = this.querySelector('input[name="accepts_marketing"]');
    this.phoneErrorElement = this.querySelector('[data-bis-phone-error]');
    this.statusElement = this.querySelector('[data-bis-status]');

    if (!this.statusElement) {
      this.statusElement = document.createElement('p');
      this.statusElement.className = 'back-in-stock__status';
      this.statusElement.setAttribute('data-bis-status', '');
      this.statusElement.setAttribute('role', 'status');
      this.statusElement.setAttribute('aria-live', 'polite');

      const body = this.querySelector('.back-in-stock__body');
      if (body) {
        body.append(this.statusElement);
      }
    }

    this.syncProductFormControls();
    this.setInitialVariantId();
    this.updateAvailabilityUi(this.getInitialAvailability());

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit);
    }

    this.addEventListener('toggle', this.onPopoverToggle);
    this.addEventListener('keydown', this.onKeyDown);

    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseClick);
    }

    if (this.phoneInput) {
      this.phoneInput.addEventListener('input', this.onPhoneInput);
    }
    this.variantEventSource = this.closest('quick-buy-drawer, quick-buy-popover') || document;
    this.variantEventSource.addEventListener('variant:changed', this.onDocumentVariantChanged);
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.onSubmit);
    }

    this.removeEventListener('toggle', this.onPopoverToggle);
    this.removeEventListener('keydown', this.onKeyDown);
    this.unlockScroll();

    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.onCloseClick);
    }

    if (this.phoneInput) {
      this.phoneInput.removeEventListener('input', this.onPhoneInput);
    }
    if (this.triggerButton) {
      this.triggerButton.removeEventListener('click', this.onTriggerClick);
    }
    this.variantEventSource?.removeEventListener('variant:changed', this.onDocumentVariantChanged);
    this.variantEventSource = null;
  }

  findProductForm() {
    const productFormId = this.getAttribute('product-form');
    const productFormContainer = this.closest('.product-form');

    if (productFormId) {
      if (productFormContainer) {
        const scopedForm = productFormContainer.querySelector(`#${CSS.escape(productFormId)}`);

        if (scopedForm?.matches('form')) {
          return scopedForm;
        }
      }

      const formById = document.getElementById(productFormId);

      if (formById?.matches('form')) {
        return formById;
      }
    }

    if (!productFormContainer) {
      return null;
    }

    return productFormContainer.querySelector('form[is="product-form"], form[id^="product-form-"]');
  }

  setInitialVariantId() {
    if (!this.productForm) {
      return;
    }

    const variantInput = this.productForm.querySelector('input[name="id"]');

    if (variantInput && variantInput.value) {
      this.currentVariantId = String(variantInput.value);
    }
  }

  onVariantChanged(event) {
    const variant = event?.detail?.variant;
    const variantId = variant?.id;

    if (variantId) {
      this.currentVariantId = String(variantId);
    }

    this.updateAvailabilityUi(variant);
  }

  onDocumentVariantChanged(event) {
    const productFormId = this.getAttribute('product-form');
    const eventTarget = event?.target;
    const eventTargetFormId = eventTarget?.getAttribute?.('id') || null;

    if (productFormId && eventTargetFormId !== productFormId) {
      return;
    }

    if (eventTarget?.matches?.('form')) {
      this.syncProductFormControls(eventTarget);
    } else {
      this.syncProductFormControls();
    }

    this.onVariantChanged(event);
  }

  syncProductFormControls(productForm = null) {
    const productFormId = this.getAttribute('product-form');
    const nextProductForm = productForm?.matches?.('form') ? productForm : this.findProductForm();
    const localContainer = this.closest('.product-form');

    let nextTriggerButton = nextProductForm?.querySelector('[data-bis-trigger]') || null;
    let nextAddToCartButton = nextProductForm?.querySelector('[data-product-add-to-cart-button]') || null;

    if ((!nextTriggerButton || !nextAddToCartButton) && productFormId) {
      const scopedRoot = localContainer || this.closest('quick-buy-drawer, quick-buy-popover, [id^="shopify-section-"]') || document;
      const escapedFormId = CSS.escape(productFormId);

      nextTriggerButton = nextTriggerButton || scopedRoot.querySelector(`[data-bis-trigger][form="${escapedFormId}"]`) || null;
      nextAddToCartButton = nextAddToCartButton || scopedRoot.querySelector(`[data-product-add-to-cart-button][form="${escapedFormId}"]`) || null;
    }

    if (!nextTriggerButton && localContainer) {
      nextTriggerButton = localContainer.querySelector('[data-bis-trigger]') || null;
    }

    if (!nextAddToCartButton && localContainer) {
      nextAddToCartButton = localContainer.querySelector('[data-product-add-to-cart-button]') || null;
    }

    if (this.triggerButton && this.triggerButton !== nextTriggerButton) {
      this.triggerButton.removeEventListener('click', this.onTriggerClick);
    }

    if (nextTriggerButton && this.triggerButton !== nextTriggerButton) {
      nextTriggerButton.addEventListener('click', this.onTriggerClick);
    }

    this.productForm = nextProductForm;
    this.triggerButton = nextTriggerButton;
    this.addToCartButton = nextAddToCartButton;
  }

  onCloseClick() {
    this.closePopover();
  }

  onTriggerClick(event) {
    this.lastTriggerButton = event?.currentTarget || this.triggerButton || null;

    if (this.isPopoverOpen()) {
      this.closePopover();
    } else {
      this.openPopover();
    }
  }

  onPhoneInput() {
    this.clearPhoneError();
    this.setStatus('');
  }

  onPopoverToggle() {
    if (this.isPopoverOpen()) {
      this.lockScroll();
      this.focusInitialElement();
    } else {
      this.unlockScroll();
      this.resetDialogForm();
      this.restoreFocusToTrigger();
    }
  }

  onKeyDown(event) {
    if (!this.isPopoverOpen() || event.key !== 'Tab') {
      return;
    }

    const focusableElements = this.getFocusableElements();

    if (focusableElements.length === 0) {
      event.preventDefault();
      this.focus({ preventScroll: true });
      return;
    }

    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (event.shiftKey && activeElement === firstFocusableElement) {
      event.preventDefault();
      lastFocusableElement.focus({ preventScroll: true });
      return;
    }

    if (!event.shiftKey && activeElement === lastFocusableElement) {
      event.preventDefault();
      firstFocusableElement.focus({ preventScroll: true });
    }
  }

  getInitialAvailability() {
    if (this.addToCartButton) {
      return { available: !this.addToCartButton.hasAttribute('disabled') };
    }

    const value = this.dataset.selectedVariantAvailable;

    if (value === 'true') {
      return { available: true };
    }

    if (value === 'false') {
      return { available: false };
    }

    return null;
  }

  updateAvailabilityUi(variant) {
    if (!this.triggerButton || !this.addToCartButton) {
      return;
    }

    const isSoldOut = Boolean(variant && variant.available === false);

    this.triggerButton.toggleAttribute('hidden', !isSoldOut);
    this.addToCartButton.toggleAttribute('hidden', isSoldOut);
    this.triggerButton.setAttribute('aria-hidden', String(!isSoldOut));
    this.addToCartButton.setAttribute('aria-hidden', String(isSoldOut));

    // Quick-buy drawer uses a standalone ATC button (outside product-payment-container),
    // so it doesn't get the theme's standard button state updates on variant change.
    this.syncStandaloneAddToCartButton(variant);

    if (!isSoldOut && this.isPopoverOpen()) {
      this.closePopover();
    }
  }

  syncStandaloneAddToCartButton(variant) {
    if (!this.addToCartButton) {
      return;
    }

    const labelElement = this.addToCartButton.querySelector('[data-product-add-to-cart-button-label]');

    if (labelElement) {
      return;
    }

    if (!variant || variant.available === false) {
      this.addToCartButton.setAttribute('disabled', 'disabled');
      this.addToCartButton.classList.remove('button--secondary');
      this.addToCartButton.classList.add('button--ternary');
      this.addToCartButton.textContent = window.themeVariables?.strings?.productFormSoldOut || 'Sold out';
      return;
    }

    this.addToCartButton.removeAttribute('disabled');
    this.addToCartButton.classList.remove('button--ternary');
    this.addToCartButton.classList.add('button--secondary');
    this.addToCartButton.textContent = this.addToCartButton.getAttribute('data-button-content') || this.addToCartButton.textContent;
  }

  isPopoverOpen() {
    return this.matches(':popover-open');
  }

  openPopover() {
    if (typeof this.showPopover === 'function') {
      this.showPopover();
      return;
    }

    this.setAttribute('open', '');
  }

  closePopover() {
    if (typeof this.hidePopover === 'function') {
      this.hidePopover();
      return;
    }

    this.removeAttribute('open');
  }

  focusInitialElement() {
    const preferredFocusTarget = this.emailInput || this.closeButton || this.getFocusableElements()[0] || this;

    window.requestAnimationFrame(() => {
      preferredFocusTarget?.focus?.({ preventScroll: true });
    });
  }

  restoreFocusToTrigger() {
    const trigger = this.lastTriggerButton;

    if (!trigger?.isConnected || trigger.hasAttribute('hidden') || trigger.hasAttribute('disabled')) {
      return;
    }

    window.requestAnimationFrame(() => {
      trigger.focus({ preventScroll: true });
    });
  }

  getFocusableElements() {
    return Array.from(
      this.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((element) => {
      if (element.hasAttribute('hidden') || element.getAttribute('aria-hidden') === 'true') {
        return false;
      }

      return element.getClientRects().length > 0;
    });
  }

  lockScroll() {
    document.documentElement.classList.add('lock-all');
    document.body?.classList.add('lock-all');
    this.closest('quick-buy-drawer, quick-buy-popover')?.querySelector('.drawer__content')?.classList.add('lock-all');
  }

  unlockScroll() {
    document.documentElement.classList.remove('lock-all');
    document.body?.classList.remove('lock-all');
    this.closest('quick-buy-drawer, quick-buy-popover')?.querySelector('.drawer__content')?.classList.remove('lock-all');
  }

  async onSubmit(event) {
    event.preventDefault();

    if (!this.form) {
      return;
    }

    // `loader-button` can set a loading state before our validation runs.
    this.setLoading(false);

    const email = this.emailInput?.value?.trim() || '';
    const phoneNumber = await normalisePhoneNumber(this.phoneInput?.value, this.dataset.defaultPhoneCountry);
    const acceptsMarketing = Boolean(this.acceptsMarketingInput?.checked);

    this.clearPhoneError();

    if (email && this.emailInput && !this.emailInput.checkValidity()) {
      this.emailInput.reportValidity();
      return;
    }

    if (!email && !phoneNumber) {
      this.setStatus(this.t('validation.emailOrPhoneRequired'), 'error');
      return;
    }

    if (this.phoneInput && this.phoneInput.value.trim() && !phoneNumber) {
      this.setPhoneError(this.t('validation.invalidPhone'));
      return;
    }

    if (phoneNumber && !this.getKlaviyoSmsListId()) {
      this.setPhoneError(this.t('errors.smsUnavailable'));
      return;
    }

    if (acceptsMarketing && !email) {
      this.setStatus(this.t('validation.emailRequiredForMarketing'), 'error');
      return;
    }

    if (acceptsMarketing && !this.getKlaviyoEmailListId()) {
      this.setStatus(this.t('errors.emailMarketingUnavailable'), 'error');
      return;
    }

    if (!this.currentVariantId) {
      this.setStatus(this.t('errors.variantUnavailable'), 'error');
      return;
    }

    const companyId = this.getKlaviyoCompanyId();

    if (!companyId) {
      this.setStatus(this.t('errors.missingCompanyId'), 'error');
      return;
    }

    this.setLoading(true);
    this.setStatus('');

    try {
      if (email) {
        await submitEmailBackInStockSubscription({
          email,
          variantId: this.currentVariantId,
          companyId,
        });
      }

      let warnings = [];

      if (phoneNumber) {
        let smsBackInStockSubmitted = false;

        try {
          await submitSmsBackInStockSubscription({
            phoneNumber,
            variantId: this.currentVariantId,
            companyId,
          });
          smsBackInStockSubmitted = true;
        } catch (error) {
          if (!email) {
            throw error;
          }

          warnings.push(this.t('warnings.smsSubscriptionFailed'));
        }

        if (smsBackInStockSubmitted) {
          try {
            await submitSmsMarketingConsent({
              phoneNumber,
              smsListId: this.getKlaviyoSmsListId(),
              companyId,
              missingSmsListMessage: this.t('errors.missingSmsListId'),
            });
          } catch (error) {
            warnings.push(this.t('warnings.smsSubscriptionFailed'));
          }
        }
      }

      if (acceptsMarketing) {
        try {
          await submitEmailMarketingConsent({
            email,
            emailListId: this.getKlaviyoEmailListId(),
            companyId,
          });
        } catch (error) {
          warnings.push(this.t('warnings.emailMarketingConsentFailed'));
        }
      }

      let successMessage = this.t('success.subscribed');
      if (email && phoneNumber) {
        successMessage = this.t('success.subscribedEmailSms');
      } else if (email) {
        successMessage = this.t('success.subscribedEmail');
      } else if (phoneNumber) {
        successMessage = this.t('success.subscribedSms');
      }

      const warningPrefix = phoneNumber && !email
        ? this.t('warnings.however')
        : this.t('warnings.partialPrefix');
      const warningMessage = warnings.length > 0
        ? ` ${warningPrefix} ${warnings.join(this.t('warnings.separator'))}.`
        : '';

      this.setStatus(`${successMessage}${warningMessage}`, 'success');
      this.form.reset();
      this.clearPhoneError();

      this.dispatchEvent(
        new CustomEvent('back-in-stock:subscribed', {
          bubbles: true,
          detail: {
            variantId: this.currentVariantId,
            channel: phoneNumber ? 'EMAIL_SMS' : 'EMAIL',
          },
        }),
      );
    } catch (error) {
      this.setStatus(error.message || this.t('errors.genericSubmitFailed'), 'error');
    } finally {
      this.setLoading(false);
    }
  }

  getKlaviyoCompanyId() {
    return this.dataset.companyId || null;
  }

  getKlaviyoSmsListId() {
    return this.dataset.smsListId || null;
  }

  getKlaviyoEmailListId() {
    return this.dataset.emailListId || null;
  }

  setLoading(isLoading) {
    if (!this.submitButton) {
      return;
    }

    this.submitButton.toggleAttribute('disabled', isLoading);
    this.submitButton.setAttribute('aria-busy', String(isLoading));
  }

  resetDialogForm() {
    this.setLoading(false);
    this.setStatus('');
    this.clearPhoneError();

    if (!this.form) {
      return;
    }

    this.form.reset();

    this.form.querySelectorAll('.input__field.is-filled').forEach((input) => {
      input.classList.remove('is-filled');
    });
  }

  setStatus(message, state = '') {
    if (!this.statusElement) {
      return;
    }

    this.statusElement.textContent = message;

    if (state) {
      this.statusElement.dataset.state = state;
    } else {
      delete this.statusElement.dataset.state;
    }
  }

  setPhoneError(message) {
    if (!this.phoneErrorElement) {
      this.setStatus(message, 'error');
      return;
    }

    this.phoneErrorElement.textContent = message;
    this.phoneErrorElement.hidden = !message;
    this.phoneInput?.setAttribute('aria-invalid', 'true');
  }

  clearPhoneError() {
    if (!this.phoneErrorElement) {
      return;
    }

    this.phoneErrorElement.textContent = '';
    this.phoneErrorElement.hidden = true;
    this.phoneInput?.removeAttribute('aria-invalid');
  }
}

if (!customElements.get('back-in-stock')) {
  customElements.define('back-in-stock', BackInStock);
}
